// app/(app)/payments/index.tsx
import { Button } from "@/components/default/forms";
import DatePicker from "@/components/default/forms/DatePicker"; // new
import SearchableSelect from "@/components/default/forms/SearchableSelect";
import { useAppTheme } from "@/hooks/useAppTheme";
import { locationApi } from "@/services/locationApi";
import { Payment } from "@/services/paymentApi";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { fetchCustomers } from "@/store/slices/customerSlice";
import { fetchPayments, resetPagination } from "@/store/slices/paymentSlice";
import { router, useFocusEffect } from "expo-router";
import { useCallback, useEffect, useState } from "react";
import {
  ActivityIndicator,
  FlatList,
  RefreshControl,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";

export default function PaymentsListScreen() {
  const { colors, spacing } = useAppTheme();
  const dispatch = useAppDispatch();
  const { payments, pagination, loading } = useAppSelector(
    (state) => state.payments,
  );
  const { customers } = useAppSelector((state) => state.customers);

  const [filterCustomerId, setFilterCustomerId] = useState<string>("");
  const [filterType, setFilterType] = useState<string>("");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  const [areas, setAreas] = useState<{ id: number; name: string }[]>([]);
  const [selectedAreaId, setSelectedAreaId] = useState<string>("");
  const [loadingAreas, setLoadingAreas] = useState(false);
  const [customerSearch, setCustomerSearch] = useState("");

  const limit = 10;

  const loadAreas = async () => {
    setLoadingAreas(true);
    try {
      const fetchedAreas = await locationApi.getAreas();
      setAreas(fetchedAreas);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingAreas(false);
    }
  };

  useEffect(() => {
    loadAreas();
    dispatch(fetchCustomers({ page: 1, limit: 100, search: "" }));
  }, []);

  useEffect(() => {
    if (selectedAreaId) {
      dispatch(
        fetchCustomers({
          page: 1,
          limit: 100,
          search: "",
          areaId: parseInt(selectedAreaId),
        }),
      );
      setFilterCustomerId("");
    }
  }, [selectedAreaId]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (selectedAreaId) {
        dispatch(
          fetchCustomers({
            page: 1,
            limit: 20,
            search: customerSearch,
            areaId: parseInt(selectedAreaId),
          }),
        );
      }
    }, 500);
    return () => clearTimeout(timeout);
  }, [customerSearch, selectedAreaId]);

  const loadPayments = (page = 1) => {
    const params: any = { page, limit };
    if (filterCustomerId) params.customerId = parseInt(filterCustomerId);
    if (filterType) params.paymentType = filterType;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    dispatch(fetchPayments(params));
  };

  useFocusEffect(
    useCallback(() => {
      loadPayments(1);
      return () => {
        dispatch(resetPagination());
      };
    }, [filterCustomerId, filterType, startDate, endDate]),
  );

  const loadMore = () => {
    if (!loading && pagination.hasNextPage) {
      loadPayments(pagination.currentPage + 1);
    }
  };

  const handleRefresh = () => {
    loadPayments(1);
  };

  const clearFilters = () => {
    setFilterCustomerId("");
    setFilterType("");
    setStartDate("");
    setEndDate("");
    setSelectedAreaId("");
  };

  const renderItem = ({ item }: { item: Payment }) => (
    <TouchableOpacity
      style={styles.card}
      onPress={() => router.push(`/(app)/payments/${item.id}`)}
      activeOpacity={0.7}
    >
      <View style={styles.cardHeader}>
        <Text style={styles.customerName}>
          {item.Customer?.fullName || "Unknown"}
        </Text>
        <Text style={styles.amount}>₹{parseFloat(item.amount).toFixed(2)}</Text>
      </View>
      <View style={styles.cardDetails}>
        <Text style={styles.typeText}>
          {item.paymentType.toUpperCase()}
          {item.referenceNo ? ` • ${item.referenceNo}` : ""}
        </Text>
        <Text style={styles.dateText}>
          {new Date(item.paymentDate).toLocaleDateString()}
        </Text>
      </View>
      <Text style={styles.createdBy}>
        By: {item.creator?.ownerName || "System"}
      </Text>
    </TouchableOpacity>
  );

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
      padding: spacing.lg,
    },
    header: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: spacing.md,
    },
    title: { fontSize: 24, fontWeight: "600", color: colors.text },
    filterRow: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: spacing.sm,
      marginBottom: spacing.md,
      alignItems: "center",
    },
    filterButton: {
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 8,
      paddingHorizontal: spacing.md,
      paddingVertical: spacing.xs,
      alignItems: "center",
    },
    filterText: { fontSize: 12, color: colors.text },
    card: {
      backgroundColor: colors.background,
      borderRadius: 12,
      padding: spacing.md,
      marginBottom: spacing.md,
      borderWidth: 1,
      borderColor: colors.border,
    },
    cardHeader: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: spacing.xs,
    },
    customerName: { fontSize: 16, fontWeight: "600", color: colors.text },
    amount: { fontSize: 16, fontWeight: "700", color: colors.primary },
    cardDetails: {
      flexDirection: "row",
      justifyContent: "space-between",
      marginBottom: spacing.xs,
    },
    typeText: { fontSize: 12, color: colors.textLight },
    dateText: { fontSize: 12, color: colors.textLight },
    createdBy: { fontSize: 12, color: colors.textLight, marginTop: 2 },
    emptyText: {
      textAlign: "center",
      color: colors.textLight,
      marginTop: spacing.xxl,
    },
  });

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}>Payments</Text>
        <Button
          title="+ New"
          onPress={() => router.push("/(app)/payments/add")}
          variant="primary"
          containerStyle={{ paddingHorizontal: 16, paddingVertical: 8 }}
        />
      </View>

      <View style={styles.filterRow}>
        <SearchableSelect
          label=""
          placeholder="Area"
          options={areas.map((a) => ({
            label: a.name,
            value: a.id.toString(),
          }))}
          value={selectedAreaId}
          onValueChange={setSelectedAreaId}
          containerStyle={{ flex: 1, marginBottom: 0 }}
          clearable
        />
        <SearchableSelect
          label=""
          placeholder="Customer"
          options={customers.map((c) => ({
            label: `${c.fullName} (${c.phone})`,
            value: c.id.toString(),
          }))}
          value={filterCustomerId}
          onValueChange={setFilterCustomerId}
          onSearch={setCustomerSearch}
          containerStyle={{ flex: 1, marginBottom: 0 }}
          clearable
          disabled={!selectedAreaId}
        />
      </View>

      <View style={styles.filterRow}>
        <SearchableSelect
          label=""
          placeholder="Type"
          options={[
            { label: "Cash", value: "cash" },
            { label: "Bank", value: "bank" },
            { label: "Damage", value: "damage" },
          ]}
          value={filterType}
          onValueChange={setFilterType}
          containerStyle={{ flex: 1, marginBottom: 0 }}
          clearable
        />
        <DatePicker
          value={startDate}
          onChange={setStartDate}
          placeholder="Start Date"
          containerStyle={{ flex: 1, marginBottom: 0 }}
        />
        <DatePicker
          value={endDate}
          onChange={setEndDate}
          placeholder="End Date"
          containerStyle={{ flex: 1, marginBottom: 0 }}
        />
        {(startDate ||
          endDate ||
          filterCustomerId ||
          filterType ||
          selectedAreaId) && (
          <TouchableOpacity
            style={[styles.filterButton, { borderColor: colors.danger }]}
            onPress={clearFilters}
          >
            <Text style={{ color: colors.danger }}>Clear</Text>
          </TouchableOpacity>
        )}
      </View>

      <FlatList
        data={payments}
        keyExtractor={(item) => item.id.toString()}
        renderItem={renderItem}
        refreshControl={
          <RefreshControl
            refreshing={loading}
            onRefresh={handleRefresh}
            tintColor={colors.primary}
          />
        }
        onEndReached={loadMore}
        onEndReachedThreshold={0.3}
        ListEmptyComponent={
          !loading ? (
            <Text style={styles.emptyText}>No payments found</Text>
          ) : null
        }
        ListFooterComponent={
          loading && payments.length > 0 ? (
            <ActivityIndicator
              size="small"
              color={colors.primary}
              style={{ marginVertical: spacing.lg }}
            />
          ) : null
        }
      />
    </View>
  );
}
